import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { devices_settings } from 'src/stores';
import type { Device } from 'src/types/Device';
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';

const LNM_CALL_METHOD = 'lnm.call';
const LNM_COMPONENT_TYPES = {
	SWITCH: 'Switch',
	LIGHT: 'Light',
	CCT: 'CCT',
	RGB: 'RGB',
	RGBW: 'RGBW',
	RGBCCT: 'RGBCCT',
} as const;

export const isLnmLocalAction = (action: ActionJob): boolean =>
	action?.method?.toLowerCase() === LNM_CALL_METHOD;

export const getLnmRemoteMethod = (action: ActionJob): string =>
	isLnmLocalAction(action) ? action.params?.method?.toLowerCase() || '' : '';

export const getLnmLocalActionMethod = (action: ActionJob): string => {
	const method = action?.method?.toLowerCase() || '';
	if (!isLnmLocalAction(action) || !action.params?.method) return method;
	return `lnm.${action.params.method.split('.')[1]?.toLowerCase()}`;
};

export const buildLnmActionJob = (controlOption: LocalControlOptions): ActionJob | null => {
	if (!controlOption?.lnm) return null;
	const params: ActionJob['params'] = { id: controlOption.lnm.componentId };
	if (controlOption.lnm.operation === 'Set') params.on = controlOption.params.on;
	return {
		method: 'LNM.Call',
		params: {
			id: controlOption.params.id,
			method: `${controlOption.lnm.componentType}.${controlOption.lnm.operation}`,
			params,
		},
	};
};

export const getLnmLocalActions = (action: ActionJob, device: Device): LocalControlOptions[] => {
	const $format = get(format);
	const settings = devices_settings.getByRealId(device.id);
	const targets = Object.keys(settings || {})
		.filter((component) => /^lnm:\d+$/.test(component))
		.map((component) => ({
			title:
				settings[component]?.name ||
				$format('lnm_instance_title', {
					values: { id: settings[component]?.id },
					default: 'LNM instance ({id})',
				}),
			value: settings[component]?.id,
		}));
	if (!targets.length) return [];

	const isLnmAction = isLnmLocalAction(action);
	const lnmId = isLnmAction ? action.params?.id : targets[0].value;
	const componentId = isLnmAction ? action.params?.params?.id : 0;
	const remoteMethod = getLnmRemoteMethod(action);
	const remoteComponentType = isLnmAction
		? action.params.method?.split('.')[0].toUpperCase()
		: 'SWITCH';
	const componentType = LNM_COMPONENT_TYPES[remoteComponentType] || LNM_COMPONENT_TYPES.SWITCH;
	const targetAttr = { target: targets };

	return [
		{
			title: $format('local_action_lnm_set_output', { default: 'LNM Set Output' }),
			value: 'lnm.set',
			attr: targetAttr,
			params: { id: lnmId, on: remoteMethod.endsWith('.set') ? action.params.params?.on : false },
			lnm: { operation: 'Set', componentType, componentId },
			onOffState: {
				checked: true,
				selectBox: false,
				label: $format('label_output_state', { default: 'Output State' }),
			},
		},
		{
			title: $format('local_action_lnm_toggle_output', { default: 'LNM Toggle Output' }),
			value: 'lnm.toggle',
			attr: targetAttr,
			params: { id: lnmId },
			lnm: { operation: 'Toggle', componentType, componentId },
		},
	];
};
